import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, OnDestroy, SimpleChanges, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiPanelComponent, UiTabGroupComponent } from '@template-ui/components';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type { UiNavigationTab } from '@template-ui/types';
import type { WorkflowDecisionInventory, WorkflowDecisionInventoryRow, WorkflowDecisionOwnerRequest, WorkflowDecisionResultRow } from './workflow-decision-inventory';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

export interface WorkflowDecisionInventoryContextEvent { context: string; }
export interface WorkflowDecisionInventoryOwnerEvent extends WorkflowDecisionInventoryContextEvent { request: WorkflowDecisionOwnerRequest; }
type InventoryView = 'results' | 'checks';

/** Read-only configuration index. Every edit remains with its existing document owner. */
@Component({
  selector: 'phms-workflow-decision-inventory',
  imports: [CommonModule, FormsModule, UiPanelComponent, UiTabGroupComponent, UiButtonDirective, UiControlDirective],
  templateUrl: './workflow-decision-inventory.component.html',
  styleUrl: './workflow-decision-inventory.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowDecisionInventoryComponent implements OnChanges, OnDestroy {
  readonly inventory = input.required<WorkflowDecisionInventory>();
  readonly context = input<(() => string) | null>(null);
  readonly active = input(true);
  readonly disabled = input(false);
  readonly canAddCheck = input(false);
  readonly initialView = input<InventoryView>('results');
  readonly ownerRequested = output<WorkflowDecisionInventoryOwnerEvent>();
  readonly backRequested = output<WorkflowDecisionInventoryContextEvent>();
  readonly addCheckRequested = output<WorkflowDecisionInventoryContextEvent>();
  readonly view = signal<InventoryView>('results');
  readonly searches = signal<Record<InventoryView, string>>({ results: '', checks: '' });
  readonly selections = signal<Record<InventoryView, string>>({ results: '', checks: '' });
  readonly stepFilter = signal('');
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private adoptedContext = '';
  private registeredContext = false;
  private focusGeneration = 0;
  private destroyed = false;

  readonly tabs = computed<UiNavigationTab[]>(() => [
    { id: 'results', label: 'Decision results', badge: String(this.inventory().counts.decisionResultPaths), badgeLabel: 'configured decision-result paths', disabled: this.disabled() },
    { id: 'checks', label: 'Transition checks', badge: String(this.inventory().counts.genericChecks), badgeLabel: 'stored generic check entries', disabled: this.disabled() },
  ]);
  readonly query = computed(() => this.searches()[this.view()]);
  readonly title = computed(() => this.view() === 'results' ? 'Decision results' : 'Transition checks');
  readonly stepOptions = computed(() => Array.from(new Map([...this.inventory().results, ...this.inventory().completionRequirements, ...this.inventory().moduleChecks].filter(row => row.nodeId !== null)
    .map(row => [row.nodeId!, row.nodeLabel || row.nodeId!])).entries()).map(([id, label]) => ({ id, label })));
  readonly visibleRows = computed<WorkflowDecisionInventoryRow[]>(() => {
    const rows = this.view() === 'results' ? this.inventory().results : this.inventory().genericChecks;
    return rows.filter(row => this.matchesFilter(row));
  });
  readonly retainedRequirements = computed(() => {
    const related = new Set(this.inventory().results.flatMap(row => row.relatedRequirementIds));
    return this.inventory().completionRequirements.filter(row => !related.has(row.id) && this.matchesFilter(row));
  });
  readonly retainedModuleChecks = computed(() => {
    const related = new Set(this.inventory().results.flatMap(row => row.relatedModuleCheckIds));
    return this.inventory().moduleChecks.filter(row => !related.has(row.id) && this.matchesFilter(row));
  });
  readonly selectionRows = computed(() => this.view() === 'checks' ? this.visibleRows()
    : [...this.visibleRows(), ...this.retainedRequirements(), ...this.retainedModuleChecks()]);
  readonly selectedRow = computed<WorkflowDecisionInventoryRow | null>(() => this.selectionRows().find(row => row.id === this.selections()[this.view()]) ?? this.selectionRows()[0] ?? null);
  readonly selectedResult = computed(() => {
    const row = this.selectedRow(); return row?.category === 'result' ? row : null;
  });
  readonly relatedRequirements = computed(() => {
    const ids = this.selectedResult()?.relatedRequirementIds ?? [];
    return this.inventory().completionRequirements.filter(row => ids.includes(row.id));
  });
  readonly relatedModuleChecks = computed(() => {
    const ids = this.selectedResult()?.relatedModuleCheckIds ?? [];
    return this.inventory().moduleChecks.filter(row => ids.includes(row.id));
  });
  readonly selectedOwners = computed(() => this.selectedRow()?.owners.filter(owner => owner.kind !== 'work-requirement' || !this.relatedRequirements().length) ?? []);
  readonly otherResults = computed(() => this.inventory().results.some(row => row.kind === 'automated-output' || row.kind === 'other-output'));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialView']?.firstChange) this.view.set(this.initialView());
    // Only trusted model reconciliation (or first explicit registration) adopts an owner.
    // Availability, local selections and callback replacement cannot renew stale authority.
    if (changes['inventory'] || !this.registeredContext && changes['context'] && this.context()) {
      this.adoptedContext = this.context()?.() ?? '';
      this.registeredContext ||= this.context() !== null;
    }
    this.focusGeneration++;
  }
  ngOnDestroy(): void { this.destroyed = true; this.focusGeneration++; }

  setView(value: string): void {
    if (!this.interactive() || value !== 'results' && value !== 'checks') return;
    this.focusGeneration++; this.view.set(value);
  }
  search(value: string): void {
    if (!this.interactive()) return;
    this.focusGeneration++; this.searches.update(searches => ({ ...searches, [this.view()]: value }));
  }
  filterStep(value: string): void {
    if (!this.interactive() || value && !this.stepOptions().some(step => step.id === value)) return;
    this.focusGeneration++; this.stepFilter.set(value);
  }
  select(row: WorkflowDecisionInventoryRow): void {
    if (!this.interactive() || !this.selectionRows().includes(row)) return;
    this.focusGeneration++; this.selections.update(selections => ({ ...selections, [this.view()]: row.id }));
  }
  requestOwner(row: WorkflowDecisionInventoryRow, request: WorkflowDecisionOwnerRequest): void {
    if (!this.canRequestOwner(row) || !row.owners.includes(request)) return;
    this.ownerRequested.emit({ context: this.adoptedContext, request: structuredClone(request) });
  }
  back(): void { if (this.ownerCurrent()) this.backRequested.emit({ context: this.adoptedContext }); }
  addCheck(): void { if (this.ownerCurrent() && this.canAddCheck()) this.addCheckRequested.emit({ context: this.adoptedContext }); }
  canRequestOwner(row: WorkflowDecisionInventoryRow): boolean {
    return this.ownerCurrent() && this.currentRow(row) && (this.selectedRow() === row
      || this.relatedRequirements().some(candidate => candidate === row) || this.relatedModuleChecks().some(candidate => candidate === row));
  }
  rowKind(row: WorkflowDecisionInventoryRow): string {
    if (row.category === 'generic-check') return row.legacy ? 'Legacy transition check' : 'Transition check';
    if (row.category !== 'result') return row.category === 'completion-requirement' ? 'Completion requirement' : 'Module-owned action check';
    return row.kind === 'recorded-decision' ? 'Recorded decision result' : row.kind === 'automated-output' ? 'Automated activity output'
      : row.kind === 'other-output' ? 'Action output' : 'Unavailable result source';
  }
  resultMovement(row: WorkflowDecisionResultRow): string {
    return row.destinationLabels.length ? `${row.isReturn === true ? 'Return to' : row.isReturn === false ? 'Continue to' : 'Configured destinations:'} ${row.destinationLabels.join(', ')}` : 'Destination unavailable or not configured';
  }

  /** Called after the parent restores this workspace; no local selection moves focus by itself. */
  focusHeading(): void { this.queueFocus(true); }
  /** Restores the current list selection, falling back to the search control for an empty view. */
  focusSelection(): void { this.queueFocus(false); }

  private interactive(): boolean { return !this.destroyed && this.active() && !this.disabled(); }
  private matchesFilter(row: WorkflowDecisionInventoryRow): boolean {
    const search = this.query().trim().toLocaleLowerCase();
    return (this.view() !== 'results' || !this.stepFilter() || row.nodeId === this.stepFilter())
      && (!search || [row.label, row.description, row.nodeLabel, row.sourcePath, ...row.details.flatMap(detail => [detail.label, detail.value]), ...row.issues]
        .some(value => value?.toLocaleLowerCase().includes(search)));
  }
  private ownerCurrent(): boolean { return this.interactive() && !!this.adoptedContext && this.context()?.() === this.adoptedContext; }
  private currentRow(row: WorkflowDecisionInventoryRow): boolean {
    const inventory = this.inventory();
    return [...inventory.results, ...inventory.genericChecks, ...inventory.completionRequirements, ...inventory.moduleChecks].includes(row);
  }
  private queueFocus(heading: boolean): void {
    const generation = ++this.focusGeneration, context = this.adoptedContext, view = this.view(), selection = this.selectedRow()?.id;
    queueMicrotask(() => {
      if (!this.ownerCurrent() || this.adoptedContext !== context || generation !== this.focusGeneration || this.ownerViews?.focusSuspended()
        || this.view() !== view || !this.element.nativeElement.isConnected) return;
      const host = this.element.nativeElement;
      const target = heading ? host.querySelector<HTMLElement>('[data-decision-heading]')
        : Array.from(host.querySelectorAll<HTMLButtonElement>('[data-decision-row]')).find(button => button.dataset['decisionRow'] === selection)
          ?? host.querySelector<HTMLInputElement>('input[type="search"]');
      if (target && !target.closest('[hidden], [inert]')) target.focus();
    });
  }
}
